/**
 * Kalkyle line ↔ Offer::Group membership helpers.
 *
 * Display convention: ungrouped lines ("Uten gruppe") render at the END of the
 * grid after all numbered chapters — keeps chapter chapters contiguous like the
 * Claude structure screenshot.
 */

import { buildGroupTree, flattenGroups } from "./offer-group-hierarchy";

export const UNGROUPED_KEY = "__ungrouped__";

/** HTML5 DnD MIME for dragging a calc line onto a structure group. */
export const LINE_DROP_MIME = "application/x-promang-calc-line";

/** Normalize nullable offer_group_id from API / form state. */
export function normalizeOfferGroupId(value) {
  if (value == null || value === "" || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Read placement from an item row — API may emit snake_case or camelCase.
 * Prefer snake_case when both are present.
 */
export function readOfferGroupId(item) {
  if (!item || typeof item !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(item, "offer_group_id")) {
    return normalizeOfferGroupId(item.offer_group_id);
  }
  if (Object.prototype.hasOwnProperty.call(item, "offerGroupId")) {
    return normalizeOfferGroupId(item.offerGroupId);
  }
  return normalizeOfferGroupId(item.offer_group_id ?? item.offerGroupId);
}

/**
 * Collect group id + all descendant ids (as strings).
 */
export function groupIdWithDescendants(flatGroups, groupId) {
  const self = groupId == null ? null : String(groupId);
  if (self == null) return new Set();
  const out = new Set([self]);
  const byParent = new Map();
  (flatGroups || []).forEach((g) => {
    if (g?.id == null) return;
    const parentKey =
      g.parent_id == null || g.parent_id === ""
        ? null
        : String(g.parent_id);
    if (parentKey == null) return;
    if (!byParent.has(parentKey)) byParent.set(parentKey, []);
    byParent.get(parentKey).push(String(g.id));
  });
  const stack = [...(byParent.get(self) || [])];
  while (stack.length) {
    const id = stack.pop();
    if (out.has(id)) continue;
    out.add(id);
    const kids = byParent.get(id);
    if (kids?.length) stack.push(...kids);
  }
  return out;
}

/**
 * Lines whose offer_group_id is `groupId` (direct members only).
 */
export function itemsInGroup(items, groupId) {
  const key = groupId == null ? null : String(groupId);
  return (items || []).filter((item) => {
    const gid = readOfferGroupId(item);
    if (key == null) return gid == null;
    return gid != null && String(gid) === key;
  });
}

/**
 * Lines in group or any descendant chapter (for kapittelsum).
 */
export function itemsInChapter(items, flatGroups, groupId) {
  const ids = groupIdWithDescendants(flatGroups, groupId);
  if (!ids.size) return [];
  return (items || []).filter((item) => {
    const gid = readOfferGroupId(item);
    return gid != null && ids.has(String(gid));
  });
}

/**
 * Build flat display rows for the calc grid when offer groups exist.
 *
 * @returns {Array<
 *   | { kind: 'group', group: object, depth: number, itemCount: number }
 *   | { kind: 'item', item: object, itemIndex: number, depth: number }
 *   | { kind: 'ungrouped', depth: number, itemCount: number }
 * >}
 */
export function buildGroupedDisplayRows(flatGroups, items, collapsedGroupIds) {
  const collapsed = collapsedGroupIds || new Set();
  const tree = buildGroupTree(flatGroups || []);
  const rows = [];
  const claimed = new Set();

  const claimSubtreeItems = (node) => {
    (items || []).forEach((item, itemIndex) => {
      const gid = readOfferGroupId(item);
      if (gid != null && String(gid) === String(node.id)) {
        claimed.add(itemIndex);
      }
    });
    (node.children || []).forEach((child) => claimSubtreeItems(child));
  };

  const walk = (nodes, depth) => {
    (nodes || []).forEach((node) => {
      const idKey = String(node.id ?? node.clientId);
      const direct = (items || [])
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(({ item }) => {
          const gid = readOfferGroupId(item);
          return gid != null && String(gid) === String(node.id);
        })
        .sort(
          (a, b) =>
            (a.item.row_order ?? a.itemIndex) - (b.item.row_order ?? b.itemIndex)
        );

      rows.push({
        kind: "group",
        group: node,
        depth,
        itemCount: direct.length,
      });

      const isCollapsed = collapsed.has(idKey);
      if (!isCollapsed) {
        direct.forEach(({ item, itemIndex }) => {
          claimed.add(itemIndex);
          rows.push({
            kind: "item",
            item,
            itemIndex,
            depth: depth + 1,
          });
        });
        if (Array.isArray(node.children) && node.children.length) {
          walk(node.children, depth + 1);
        }
      } else {
        // Claim entire subtree so nested lines don't leak into «Uten gruppe».
        claimSubtreeItems(node);
      }
    });
  };

  walk(tree, 0);

  // Ungrouped bucket at END (see file comment).
  const ungrouped = (items || [])
    .map((item, itemIndex) => ({ item, itemIndex }))
    .filter(({ item, itemIndex }) => {
      if (claimed.has(itemIndex)) return false;
      return readOfferGroupId(item) == null;
    })
    .sort(
      (a, b) =>
        (a.item.row_order ?? a.itemIndex) - (b.item.row_order ?? b.itemIndex)
    );

  // Also orphan lines pointing at missing groups → treat as ungrouped visually
  const knownIds = new Set(
    flattenGroups(flatGroups || [])
      .filter((g) => g?.id != null)
      .map((g) => String(g.id))
  );
  const orphans = (items || [])
    .map((item, itemIndex) => ({ item, itemIndex }))
    .filter(({ item, itemIndex }) => {
      if (claimed.has(itemIndex)) return false;
      const gid = readOfferGroupId(item);
      if (gid == null) return false;
      return !knownIds.has(String(gid));
    })
    .sort(
      (a, b) =>
        (a.item.row_order ?? a.itemIndex) - (b.item.row_order ?? b.itemIndex)
    );

  const loose = [...ungrouped, ...orphans];
  if (loose.length || (flatGroups || []).length > 0) {
    rows.push({
      kind: "ungrouped",
      depth: 0,
      itemCount: loose.length,
    });
    if (!collapsed.has(UNGROUPED_KEY)) {
      loose.forEach(({ item, itemIndex }) => {
        rows.push({
          kind: "item",
          item,
          itemIndex,
          depth: 1,
        });
      });
    }
  }

  return rows;
}

/**
 * Whether workspace should render synthetic group headers.
 */
export function shouldShowGroupHierarchy(flatGroups) {
  return Array.isArray(flatGroups) && flatGroups.length > 0;
}

/**
 * Move a calc / tilbudslinje within the same array and rewrite row_order.
 * Same-index and out-of-range calls return the original array.
 */
export function reorderCalcItems(items, fromIndex, toIndex) {
  if (!Array.isArray(items)) return items;
  const from = Number(fromIndex);
  const to = Number(toIndex);
  if (
    !Number.isInteger(from) ||
    !Number.isInteger(to) ||
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to >= items.length
  ) {
    return items;
  }
  const next = [...items];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next.map((item, i) => ({ ...item, row_order: i }));
}
